import { useState, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

// Get the theme directory URL for assets
const getThemeAssetUrl = (assetPath) => {
    // Check if we're in WordPress environment
    if (typeof window !== 'undefined' && window.wp && window.wp.data) {
        // Try to get theme URL from WordPress
        const themeUrl = window.purocleanData?.themeUrl || '/wp-content/themes/puroclean-tw';
        return `${themeUrl}/${assetPath}`;
    }
    // Fallback for development/preview
    return `/wp-content/themes/puroclean-tw/${assetPath}`;
};

// Pool of dummy testimonials for development/fallback
const DUMMY_TESTIMONIALS_POOL = [
    {
        text: "Thomas was able to help my family on short notice. He patiently explained what we needed and the method for cleaning. They worked quietly under strained conditions (death of a family member) and the room was clean, sanitized and smelled fresh. We will indeed use this company again.",
        author: "JACKIE T. - Coral Gables, FL"
    },
    {
        text: "I want to give a major shout out to the guys in Beaverton/Portland, Oregon. We had a pipe break in our very large crawl space. It caused so much water and mold damage that our entire bottom 2 floors had to be removed down to the subfloor.",
        author: "KATIE - Portland, Oregon"
    },
    {
        text: "PuroClean exceeded our expectations during a very stressful time. The team was professional, efficient, and communicated with us every step of the way. Highly recommend their services!",
        author: "MICHAEL R. - Tampa, FL"
    },
    {
        text: "The response time was incredible - they were at our property within 2 hours of our call. The crew was professional, courteous, and explained every step of the process. Our home was restored to better than before condition.",
        author: "SARAH M. - Orlando, FL"
    },
    {
        text: "After our basement flooded, PuroClean handled everything from water extraction to complete restoration. They worked with our insurance company directly and made the whole process stress-free for us.",
        author: "DAVID L. - Tampa Bay, FL"
    },
    {
        text: "Outstanding service! The team arrived quickly, assessed the damage thoroughly, and had our office back to normal in just a few days. Minimal disruption to our business operations.",
        author: "JENNIFER K. - Miami, FL"
    },
    {
        text: "When our kitchen suffered severe water damage, PuroClean's team worked around the clock to minimize disruption. Their attention to detail and commitment to excellence was evident in every interaction.",
        author: "ROBERT H. - Jacksonville, FL"
    },
    {
        text: "From the initial assessment to final cleanup, PuroClean handled our mold remediation with professionalism and expertise. They kept us informed every step of the way and delivered exactly what they promised.",
        author: "MARIA S. - Fort Lauderdale, FL"
    }
];

// Function to get random testimonials for development/fallback
const getDummyTestimonials = (count = 3) => {
    // Shuffle the pool and take the first 'count' items
    const shuffled = [...DUMMY_TESTIMONIALS_POOL].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.min(count, DUMMY_TESTIMONIALS_POOL.length));
};

const TestimonialsCarousel = ({
    testimonialType = 'client',
    count = 3,
    autoplay = false,
    autoplaySpeed = 5000,
    showDots = true,
    showArrows = false,
    randomOrder = true,
    className = '',
    layout = 'card',
    testimonials = null,
    isEditor = false
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [testimonialData, setTestimonialData] = useState([]);
    const [loading, setLoading] = useState(!testimonials);
    const sliderRef = useRef(null);
    const autoplayRef = useRef(null);
    
    // Touch handling for mobile swipe - moved to top level to avoid hooks violation
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    // Fetch testimonials from API
    useEffect(() => {
        if (testimonials) {
            setTestimonialData(testimonials);
            setLoading(false);
            return;
        }

        
        if (isEditor) {
            // In editor, use dummy data for preview
            setTestimonialData(getDummyTestimonials(count));
            setLoading(false);
            return;
        }

        // Fetch real data from WordPress REST API
        const fetchTestimonials = async () => {
            try {
                const response = await fetch(
                    `/wp-json/wp/v2/reviews?per_page=${count}&orderby=${randomOrder ? 'rand' : 'date'}&meta_query[0][key]=testimonial_type&meta_query[0][value]=${testimonialType}&meta_query[0][compare]==`
                );
                
                if (response.ok) {
                    const reviews = await response.json();
                    const formattedTestimonials = reviews.map(review => ({
                        text: review.acf?.testimonial_quote || review.content?.rendered || '',
                        author: review.title?.rendered?.toUpperCase() + 
                               (review.acf?.testimonial_location ? ` - ${review.acf.testimonial_location}` : '')
                    })).filter(t => t.text); // Only include testimonials with text
                    
                    // If no testimonials found, use dummy data for development
                    if (formattedTestimonials.length === 0) {
                        setTestimonialData(getDummyTestimonials(count));
                    } else {
                        setTestimonialData(formattedTestimonials);
                    }
                }
            } catch (error) {
                console.error('Error fetching testimonials:', error);
                // Fallback to dummy data for development
                setTestimonialData(getDummyTestimonials(count));
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, [testimonials, testimonialType, count, randomOrder, isEditor]);

    // Autoplay functionality
    useEffect(() => {
        if (!autoplay || testimonialData.length <= 1) return;

        autoplayRef.current = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % testimonialData.length);
        }, autoplaySpeed);

        return () => {
            if (autoplayRef.current) {
                clearInterval(autoplayRef.current);
            }
        };
    }, [autoplay, autoplaySpeed, testimonialData.length]);

    // Handle slide change
    const goToSlide = (index) => {
        setCurrentSlide(index);
        if (sliderRef.current) {
            const slideWidth = sliderRef.current.offsetWidth;
            sliderRef.current.scrollTo({
                left: slideWidth * index,
                behavior: 'smooth'
            });
        }
        
        // Reset autoplay
        if (autoplayRef.current) {
            clearInterval(autoplayRef.current);
            if (autoplay) {
                autoplayRef.current = setInterval(() => {
                    setCurrentSlide(prev => (prev + 1) % testimonialData.length);
                }, autoplaySpeed);
            }
        }
    };

    // Handle scroll for manual navigation
    const handleScroll = () => {
        if (!sliderRef.current) return;
        
        const slideWidth = sliderRef.current.offsetWidth;
        const scrollLeft = sliderRef.current.scrollLeft;
        const newSlide = Math.round(scrollLeft / slideWidth);
        
        if (newSlide !== currentSlide) {
            setCurrentSlide(newSlide);
        }
    };

    // Navigation functions
    const nextSlide = () => {
        const next = (currentSlide + 1) % testimonialData.length;
        goToSlide(next);
    };

    const prevSlide = () => {
        const prev = (currentSlide - 1 + testimonialData.length) % testimonialData.length;
        goToSlide(prev);
    };

    // Get layout-specific classes
    const getContainerClasses = () => {
        const base = "relative overflow-hidden rounded-lg";
        switch (layout) {
            case 'minimal':
                return `${base} bg-transparent`;
            case 'featured':
                return `${base} bg-gradient-to-br from-secondary to-blue-800 text-white min-h-[350px] rounded-2xl shadow-lg`;
            default: // card
                return `${base} bg-secondary text-white min-h-[300px]`;
        }
    };

    const getContentClasses = () => {
        const base = "select-none h-full flex flex-col justify-center";
        switch (layout) {
            case 'minimal':
                return `${base} p-4 pb-12`;
            case 'featured':
                return `${base} p-12 pb-20 md:p-8 md:pb-16`;
            default: // card
                return `${base} p-8 pb-20 md:p-6 md:pb-16`;
        }
    };

    const getTextClasses = () => {
        const base = "select-none italic leading-relaxed mb-6 text-inherit";
        switch (layout) {
            case 'minimal':
                return `${base} text-lg md:text-base`;
            case 'featured':
                return `${base} text-[22px] md:text-lg`;
            default: // card
                return `${base} text-xl md:text-lg`;
        }
    };

    const getAuthorClasses = () => {
        const base = "select-none not-italic font-semibold text-inherit opacity-90";
        switch (layout) {
            case 'minimal':
                return `${base} text-base md:text-sm`;
            case 'featured':
                return `${base} text-xl font-bold md:text-base md:font-semibold`;
            default: // card
                return `${base} text-lg md:text-base`;
        }
    };

    const getDotsClasses = () => {
        const base = "hidden lg:flex justify-center gap-2 p-4 absolute bottom-0 left-0 right-0 rounded-b-lg";
        switch (layout) {
            case 'minimal':
                return `${base} bg-black/10`;
            default:
                return `${base} bg-black/40`;
        }
    };

    const getDotClasses = (isActive) => {
        const base = "w-2 h-2 rounded-full border-0 cursor-pointer transition-all duration-200 hover:scale-125 focus:outline-2 focus:outline-white focus:outline-offset-2 motion-reduce:transition-none";
        const activeClass = isActive ? "bg-white scale-125" : "";
        
        switch (layout) {
            case 'minimal':
                return `${base} ${isActive ? 'bg-blue-600' : 'bg-black/30 hover:bg-blue-600'} ${activeClass} focus:outline-blue-600`;
            default:
                return `${base} ${isActive ? 'bg-white' : 'bg-white/65 hover:bg-white'} ${activeClass}`;
        }
    };

    if (loading) {
        return (
            <div className={`relative w-full min-h-[200px] flex items-center justify-center ${className}`}>
                <div className="p-8 text-center text-gray-600 italic">
                    {__('Loading testimonials...', 'puroclean')}
                </div>
            </div>
        );
    }

    // Since we always fallback to dummy data, this should never happen
    // but keeping as safety net
    if (!testimonialData.length) {
        return (
            <div className={`relative w-full min-h-[200px] flex items-center justify-center ${className}`}>
                <div className="p-8 text-center text-gray-600 italic">
                    {__('Loading testimonials...', 'puroclean')}
                </div>
            </div>
        );
    }

    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && testimonialData.length > 1) {
            nextSlide();
        }
        if (isRightSwipe && testimonialData.length > 1) {
            prevSlide();
        }
    };

    return (
        <div 
            className={`relative w-full ${className}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Navigation Arrows */}
            {showArrows && testimonialData.length > 1 && (
                <>
                    <button 
                        className="absolute top-1/2 -translate-y-1/2 left-2.5 bg-black/50 text-white border-0 w-10 h-10 rounded-full text-xl font-bold cursor-pointer z-10 transition-all duration-200 flex items-center justify-center hover:bg-black/70 hover:scale-110 focus:outline-2 focus:outline-white focus:outline-offset-2 hidden lg:flex motion-reduce:transition-none contrast-more:bg-black contrast-more:border-2 contrast-more:border-white"
                        onClick={prevSlide}
                        aria-label={__('Previous testimonial', 'puroclean')}
                    >
                        ‹
                    </button>
                    <button 
                        className="absolute top-1/2 -translate-y-1/2 right-2.5 bg-black/50 text-white border-0 w-10 h-10 rounded-full text-xl font-bold cursor-pointer z-10 transition-all duration-200 flex items-center justify-center hover:bg-black/70 hover:scale-110 focus:outline-2 focus:outline-white focus:outline-offset-2 hidden lg:flex motion-reduce:transition-none contrast-more:bg-black contrast-more:border-2 contrast-more:border-white"
                        onClick={nextSlide}
                        aria-label={__('Next testimonial', 'puroclean')}
                    >
                        ›
                    </button>
                </>
            )}

            {/* Testimonials Container */}
            <div className={getContainerClasses()}>
                {/* Quotes Icon */}
                <div className="relative -mt-4 ml-8 mb-6 z-10">
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="80" 
                        height="80" 
                        viewBox="0 0 117.809 121.505"
                        className="w-20 h-20"
                        aria-hidden="true"
                    >
                        <path 
                            d="M79.654-586.495H131.4v-63.243H111.686c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L110.865-706l-10.677,14.373C81.707-666.576,79.654-660,79.654-630.847Zm-63.654,0H67.744v-63.243H48.032c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L47.211-706,36.534-691.627C18.053-666.576,16-660,16-630.847Z" 
                            transform="translate(-15 707)" 
                            fill="none" 
                            stroke="#f6f6f6" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2"
                        />
                    </svg>
                </div>

                <div 
                    ref={sliderRef}
                    className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-smooth motion-reduce:scroll-auto cursor-grab active:cursor-grabbing"
                    onScroll={handleScroll}
                >
                    {testimonialData.map((testimonial, index) => (
                        <div key={index} className="flex-none w-full snap-start snap-always">
                            <div className={getContentClasses()}>
                                <blockquote className="border-0 m-0 p-0">
                                    <p className={getTextClasses()}>
                                        "{testimonial.text}"
                                    </p>
                                    <cite className={getAuthorClasses()}>
                                        {testimonial.author}
                                    </cite>
                                </blockquote>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination Dots */}
                {showDots && testimonialData.length > 1 && (
                    <div className={getDotsClasses()}>
                        {testimonialData.map((_, index) => (
                            <button
                                key={index}
                                className={getDotClasses(index === currentSlide)}
                                onClick={() => goToSlide(index)}
                                aria-label={__(`Go to testimonial ${index + 1}`, 'puroclean')}
                            />
                        ))}
                    </div>
                )}

                {/* Swipe Hint for Mobile */}
                {testimonialData.length > 1 && (
                    <div className="flex lg:hidden justify-center items-center p-4 absolute bottom-0 left-0 right-0 bg-black/40 rounded-b-lg select-none">
                        <span className="text-white text-xl font-semibold underline select-none">
                            {__('Swipe for more', 'puroclean')}
                        </span>
                        <span className="ml-2 font-bold select-none">→</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TestimonialsCarousel; 